import React, { useState, useEffect } from "react";
import { Button, Typography, Space } from "antd";
import { GoogleOutlined } from "@ant-design/icons";
import { auth } from "../../firebase/firebase-config";
import {
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  User as FirebaseUser,
} from "firebase/auth";
import "./Auth.css";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import { AuthMetrics } from "src/firebase/metrics";
import { handleAuthErrors } from "src/error-handling/authErrorHandler";
import { AppUser } from "src/utils/AppUser";

const { Title, Text } = Typography;

type UserData = {
  uid: string;
  email?: string | null;
  displayName: string | null;
  photoURL: string | null;
};

type AuthPageProps = {
  user: AppUser | null;          // <-- Add user prop here
  setUser: (user: AppUser | null) => void;
};

const AuthPage: React.FC<AuthPageProps> = ({ user, setUser }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const provider = new GoogleAuthProvider();

  const handleVisitorLogin = () => {
    const visitorUser = {
      uid: "visitor",
      email: null,
      displayName: "Visitor",
      photoURL: "",
    };
    setUser(visitorUser);
    navigate("/");
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser: FirebaseUser | null) => {
      if (authUser) {
        const formattedUser: AppUser = {
          uid: authUser.uid,
          email: authUser.email ?? null,
          displayName: authUser.displayName,
          photoURL: authUser.photoURL,
        };
        setUser(formattedUser);
        navigate("/");
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, setUser]);

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, provider);
      ReactGA.event(AuthMetrics.SIGNIN);
      // user will be set via onAuthStateChanged
    } catch (error: unknown) {
      handleAuthErrors(error, "signInError");
      setUser(null);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="page-container">
      <div className="app-description">
        <div className="page-title">
          <Title level={1} style={{ fontSize: 40, fontWeight: 700, textAlign: "center", marginBottom: 8 }}>
            Welcome to Spanish Checkpoint
          </Title>
          <Title level={3} style={{ fontSize: 24, fontWeight: 600, textAlign: "center", marginBottom: 32 }}>
            ¡Bienvenido! 🎉 We're so happy to have you here.
          </Title>
          <Text style={{ display: "block", fontSize: 17, lineHeight: 1.8, textAlign: "center", maxWidth: 640, margin: "0 auto 24px" }}>
            You've unlocked your free trial of Spanish Checkpoint.<br />
            Get ready to explore Spanish with clarity and confidence!<br /><br />
            <strong>✔️ Clear grammar explanations</strong><br />
            <strong>✔️ Practical, real-world examples</strong><br />
            <strong>✔️ Easy conjugation tables</strong><br />
            <strong>✔️ Fun, effective quizzes</strong><br /><br />
            Learn at your pace, one checkpoint at a time.
          </Text>
        </div>

        <div className="auth-card">
          <div className="auth-card-content">
            <Title level={2} className="auth-card-title">
              {user ? "Welcome Back!" : "Sign In / Sign Up"}
            </Title>
            <Text type="secondary" className="auth-card-subtitle">
              {user
                ? "You're already signed in. Let's keep learning!"
                : "Sign in to unlock your personalized Spanish journey."}
            </Text>
            <Space direction="vertical" size="large" className="auth-card-buttons" style={{ width: "100%" }}>
              {!user ? (
                <>
                  <Button
                    type="primary"
                    icon={<GoogleOutlined />}
                    size="large"
                    block
                    onClick={signInWithGoogle}
                    className="google-button"
                  >
                    Sign in with Google
                  </Button>
                </>
              ) : (
                <Text type="success">You are signed in as {user.displayName || "User"}</Text>
              )}
            </Space>
            <div style={{
              marginTop: 16,
              padding: 16,
              borderRadius: 8,
              textAlign: "center",
              backgroundColor: "#fafafa",
              maxWidth: 400,
              marginLeft: "auto",
              marginRight: "auto"
            }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>Just browsing?</Text>
              <Text type="secondary">You can explore the app as a visitor.</Text>
              <Button
                type="text"
                block
                size="large"
                onClick={handleVisitorLogin}
                style={{ marginTop: 12, backgroundColor: "rgb(214 220 233)", fontSize: "14px" }}
              >
                Continue as Visitor
              </Button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
